import { api } from './client';

/** PMS securities-master classification taxonomy — the "lens model" (#PMS).
 *  Reads are open to any authenticated user; Admin writes arrive with Inc 3.
 *  Design: decisions/2026-07-20_securities-master-classification-design.md. */

export interface ClassificationTier {
  tier_level: number;
  name: string;
}

export interface ClassificationDimension {
  code: string;
  name: string;
  scope: 'universal' | 'scoped' | string;
  sort_order: number;
  tiers: ClassificationTier[];
  /** For a scoped dimension: the Asset-Class value labels it applies within. Empty for universal. */
  applies_to: string[];
}

export interface ClassificationValue {
  id: string;
  label: string;
  code: string | null;
  tier_level: number;
  parent_id: string | null;
  sort_order: number;
}

// ── views layer (migration 0094) ──────────────────────────────────────────────

export interface ClassificationSchemeLens {
  dimension_code: string;
  dimension_name: string;
  position: number;
}
export interface ClassificationScheme {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  includes_all_lenses: boolean;
  lenses: ClassificationSchemeLens[];
}
export interface ActiveViewDimension {
  code: string;
  name: string;
  scope: string;
  sort_order: number;
  position: number;
}
export interface ActiveView {
  scheme_id: string;
  scheme_name: string;
  includes_all_lenses: boolean;
  /** The resolved, ordered dimension list a consumer should display. */
  dimensions: ActiveViewDimension[];
}

export interface SchemeCreate {
  name: string;
  description?: string | null;
  includes_all_lenses?: boolean;
}
export interface SchemeUpdate {
  name?: string;
  description?: string | null;
  includes_all_lenses?: boolean;
}

export type DimensionScope = 'universal' | 'scoped';

export interface DimensionCreate {
  code: string;
  name: string;
  scope?: DimensionScope;
  sort_order?: number;
}
export interface DimensionUpdate {
  name?: string;
  scope?: DimensionScope;
  sort_order?: number;
}
export interface ValueCreate {
  tier_level: number;
  label: string;
  code?: string | null;
  parent_id?: string | null;
  sort_order?: number;
}
export interface ValueUpdate {
  label?: string;
  code?: string | null;
  parent_id?: string | null;
  sort_order?: number;
}

const enc = encodeURIComponent;

export const classificationApi = {
  // reads
  dimensions: () => api.get<ClassificationDimension[]>('/classification/dimensions'),
  values: (code: string) =>
    api.get<ClassificationValue[]>(`/classification/dimensions/${enc(code)}/values`),

  // views layer reads (migration 0094)
  schemes: () => api.get<ClassificationScheme[]>('/classification/schemes'),
  activeScheme: () => api.get<ActiveView>('/classification/schemes/active'),

  // views layer writes (classification.manage)
  createScheme: (b: SchemeCreate) => api.post<ClassificationScheme>('/classification/schemes', b),
  updateScheme: (id: string, b: SchemeUpdate) =>
    api.patch<ClassificationScheme>(`/classification/schemes/${enc(id)}`, b),
  deleteScheme: (id: string) => api.delete<ClassificationScheme>(`/classification/schemes/${enc(id)}`),
  activateScheme: (id: string) =>
    api.post<ClassificationScheme>(`/classification/schemes/${enc(id)}/activate`, {}),
  setSchemeLenses: (id: string, dimensionCodes: string[]) =>
    api.put<ClassificationScheme>(`/classification/schemes/${enc(id)}/lenses`, {
      dimension_codes: dimensionCodes,
    }),

  // dimension writes (classification.manage)
  createDimension: (b: DimensionCreate) => api.post<ClassificationDimension>('/classification/dimensions', b),
  updateDimension: (code: string, b: DimensionUpdate) =>
    api.patch<ClassificationDimension>(`/classification/dimensions/${enc(code)}`, b),
  deleteDimension: (code: string) =>
    api.delete<ClassificationDimension>(`/classification/dimensions/${enc(code)}`),
  setScope: (code: string, assetClassValueIds: string[]) =>
    api.put<ClassificationDimension>(`/classification/dimensions/${enc(code)}/scope`, {
      asset_class_value_ids: assetClassValueIds,
    }),

  // tier writes (classification.manage)
  addTier: (code: string, name: string) =>
    api.post<ClassificationTier>(`/classification/dimensions/${enc(code)}/tiers`, { name }),
  renameTier: (code: string, level: number, name: string) =>
    api.patch<ClassificationTier>(`/classification/dimensions/${enc(code)}/tiers/${level}`, { name }),
  deleteTier: (code: string, level: number) =>
    api.delete<ClassificationTier>(`/classification/dimensions/${enc(code)}/tiers/${level}`),

  // value writes (classification.manage)
  createValue: (code: string, b: ValueCreate) =>
    api.post<ClassificationValue>(`/classification/dimensions/${enc(code)}/values`, b),
  updateValue: (id: string, b: ValueUpdate) =>
    api.patch<ClassificationValue>(`/classification/values/${enc(id)}`, b),
  deleteValue: (id: string) => api.delete<ClassificationValue>(`/classification/values/${enc(id)}`),
};
